import clsx from "clsx";
import {
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { getClothesDetailByIdApi } from "../../apis/clothing.api";
import { CategoryDropdown } from "../../components/CategoryDropdown";
import { NavBar } from "../../components/NavBar";
import { TagBadge } from "../../components/TagBadge";
import { ClothingDetailByIdDto } from "../../dto/clothing/clothing-detail-by-id.dto";
import { GetClothingDetailByIdDto } from "../../dto/clothing/get-clothing-detail-by-id.dto";
import { useRequest } from "../../lib/request/use-request";
import * as styles from "./index.module.scss";

type ColumnCount = 1 | 2;

export function ClothingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get("store") ?? undefined;
  const [columnCount, setColumnCount] = useState<ColumnCount>(1);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);

  const { data: detail, requestHandler: fetchDetail } = useRequest<
    ClothingDetailByIdDto,
    GetClothingDetailByIdDto
  >({
    api: getClothesDetailByIdApi,
    title: "获取服装详情",
  });

  const handleBack = useCallback(() => {
    if (detail) {
      const params = new URLSearchParams();
      params.set("category", detail.category.id);
      if (storeId) params.set("store", storeId);
      void navigate(`/clothing?${params.toString()}`);
    } else {
      void navigate("/");
    }
  }, [detail, navigate, storeId]);

  const handleGoToStore = useCallback(
    (storeId: string) => {
      if (!detail) return;
      const params = new URLSearchParams();
      params.set("category", detail.category.id);
      params.set("store", storeId);
      void navigate(`/clothing?${params.toString()}`);
    },
    [detail, navigate],
  );

  const coverImg = useMemo(() => {
    if (!detail) return;
    if (detail.coverImg) return detail.coverImg;
    if (detail.imgs.length === 0) return;
    return detail.imgs[0];
  }, [detail]);

  const totalImgs = detail?.imgs.length ?? 0;
  const previewImg =
    previewIndex !== null ? detail?.imgs[previewIndex] : undefined;

  const closePreview = useCallback(() => {
    setPreviewIndex(null);
  }, []);

  const goPrevPreview = useCallback(() => {
    setPreviewIndex((current) =>
      current === null || current <= 0 ? current : current - 1,
    );
  }, []);

  const goNextPreview = useCallback(() => {
    setPreviewIndex((current) =>
      current === null || current >= totalImgs - 1 ? current : current + 1,
    );
  }, [totalImgs]);

  const handlePreviewTouchStart = useCallback((event: TouchEvent) => {
    if (event.touches.length !== 1) {
      touchStartRef.current = null;
      return;
    }
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    swipedRef.current = false;
  }, []);

  const handlePreviewTouchEnd = useCallback(
    (event: TouchEvent) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;

      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const SWIPE_THRESHOLD = 40;

      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        swipedRef.current = true;
        if (dx > 0) {
          goPrevPreview();
        } else {
          goNextPreview();
        }
      }
    },
    [goPrevPreview, goNextPreview],
  );

  const handlePreviewImgClick = useCallback(() => {
    if (swipedRef.current) {
      swipedRef.current = false;
      return;
    }
    closePreview();
  }, [closePreview]);

  useEffect(() => {
    if (!id) return;
    void fetchDetail({ param: { clothesId: id } });
  }, [id, fetchDetail]);

  if (!detail) {
    return (
      <div className={styles.clothingDetailPage}>
        <NavBar
          title="服装详情"
          fallbackPath="/clothing"
          onBack={handleBack}
          right={<CategoryDropdown />}
        />
        <div className={styles.clothingDetailPlaceholder}>加载中...</div>
      </div>
    );
  }

  return (
    <div className={styles.clothingDetailPage}>
      <NavBar
        title="服装详情"
        fallbackPath="/clothing"
        onBack={handleBack}
        right={<CategoryDropdown activeId={detail.category.id} />}
      />

      <section className={styles.clothingDetailInfo}>
        <h1 className={styles.clothingDetailName}>{detail.name}</h1>

        <div className={styles.clothingDetailMetaRow}>
          <span className={styles.clothingDetailMetaLabel}>分类</span>
          <Link
            to={`/?category=${detail.category.id}`}
            className={styles.clothingDetailMetaLink}
          >
            <span className={styles.clothingDetailMetaLinkText}>
              {detail.category.name}
            </span>
          </Link>
        </div>

        <div className={styles.clothingDetailMetaRow}>
          <span className={styles.clothingDetailMetaLabel}>编号</span>
          <span className={styles.clothingDetailMetaValue}>{detail.code}</span>
        </div>

        <div className={styles.clothingDetailDescription}>
          <div className={styles.clothingDetailMetaLabel}>介绍</div>
          <p className={styles.clothingDetailDescriptionText}>
            {detail.description || "暂无介绍"}
          </p>
        </div>
      </section>

      <div className={styles.clothingDetailCover}>
        {!!coverImg && (
          <img
            src={coverImg.file.url}
            alt={detail.name}
            className={styles.clothingDetailCoverImg}
            onClick={() => setPreviewIndex(0)}
          />
        )}
      </div>

      <section className={styles.clothingDetailGallery}>
        <header className={styles.clothingDetailGalleryHeader}>
          <h2 className={styles.clothingDetailGalleryTitle}>全部图片</h2>
        </header>

        {detail.imgs.length === 0 ? (
          <div className={styles.clothingDetailGalleryEmpty}>暂无图片</div>
        ) : (
          <div
            className={styles.clothingDetailWaterfall}
            style={{ columnCount }}
          >
            {detail.imgs.map((img, index) => (
              <div key={img.id} className={styles.clothingDetailWaterfallItem}>
                <img
                  src={img.file.url}
                  alt={detail.name}
                  loading="lazy"
                  className={styles.clothingDetailWaterfallImg}
                  onClick={() => setPreviewIndex(index)}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {detail.storeList.length > 0 && (
        <section className={styles.clothingDetailStores}>
          <h2 className={styles.clothingDetailStoresTitle}>所属店铺</h2>
          <ul className={styles.clothingDetailStoresList}>
            {detail.storeList.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className={styles.clothingDetailStoreItem}
                  onClick={() => handleGoToStore(entry.storeId)}
                >
                  <div className={styles.clothingDetailStoreLogo}>
                    {entry.store.logoUrl ? (
                      <img
                        src={entry.store.logoUrl}
                        alt={entry.store.name}
                        className={styles.clothingDetailStoreLogoImg}
                      />
                    ) : (
                      <span className={styles.clothingDetailStoreLogoFallback}>
                        {entry.store.name.slice(0, 1)}
                      </span>
                    )}
                  </div>
                  <div className={styles.clothingDetailStoreMain}>
                    <div className={styles.clothingDetailStoreName}>
                      {entry.store.name}
                    </div>
                    {!!entry.store.address && (
                      <div className={styles.clothingDetailStoreAddress}>
                        {entry.store.address}
                      </div>
                    )}
                    {!!entry.notes && (
                      <div className={styles.clothingDetailStoreNotes}>
                        备注: {entry.notes}
                      </div>
                    )}
                  </div>
                  <span
                    className={styles.clothingDetailStoreChevron}
                    aria-hidden="true"
                  >
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.tags.length > 0 && (
        <section className={styles.clothingDetailTags}>
          <div className={styles.clothingDetailTagsLabel}>标签</div>
          <div className={styles.clothingDetailTagsList}>
            {detail.tags.map((item) => (
              <TagBadge key={item.id} name={item.tag.name} />
            ))}
          </div>
        </section>
      )}

      {detail.imgs.length > 0 && (
        <div
          className={styles.clothingDetailColumnToggle}
          role="radiogroup"
          aria-label="瀑布流列数"
        >
          {([1, 2] as ColumnCount[]).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={columnCount === value}
              className={clsx(
                styles.clothingDetailColumnToggleItem,
                columnCount === value &&
                  styles.clothingDetailColumnToggleItemActive,
              )}
              onClick={() => setColumnCount(value)}
            >
              {value}列
            </button>
          ))}
        </div>
      )}

      {previewIndex !== null && !!previewImg && (
        <div
          className={styles.clothingDetailPreview}
          role="dialog"
          aria-modal="true"
          aria-label="图片预览"
          onTouchStart={handlePreviewTouchStart}
          onTouchEnd={handlePreviewTouchEnd}
        >
          <button
            type="button"
            aria-label="上一张"
            className={clsx(
              styles.clothingDetailPreviewNav,
              styles.clothingDetailPreviewNavPrev,
            )}
            disabled={previewIndex === 0}
            onClick={goPrevPreview}
          >
            <svg
              viewBox="0 0 24 24"
              width="28"
              height="28"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M15 5l-7 7 7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <img
            src={previewImg.file.url}
            alt={detail.name}
            className={styles.clothingDetailPreviewImg}
            onClick={handlePreviewImgClick}
          />

          <button
            type="button"
            aria-label="下一张"
            className={clsx(
              styles.clothingDetailPreviewNav,
              styles.clothingDetailPreviewNavNext,
            )}
            disabled={previewIndex === totalImgs - 1}
            onClick={goNextPreview}
          >
            <svg
              viewBox="0 0 24 24"
              width="28"
              height="28"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M9 5l7 7-7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className={styles.clothingDetailPreviewCounter}>
            {previewIndex + 1} / {totalImgs}
          </div>
        </div>
      )}
    </div>
  );
}
